import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { diffLines } from "diff";
import {
  ArrowLeft, Library, Loader2, Trash2, FileText, Brain, Plus, Eye, X, Calendar,
  History, RotateCcw, Lock, Crown, Edit3, Check
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ACCENT = "#7EE787";
const ACCENT_DEEP = "#43A047";

function authHeaders() {
  try {
    const raw = sessionStorage.getItem("user_data");
    if (!raw) return {};
    const d = JSON.parse(raw);
    return d?.session_token ? { Authorization: `Bearer ${d.session_token}` } : {};
  } catch { return {}; }
}
function isLoggedIn() {
  try { return !!JSON.parse(sessionStorage.getItem("user_data") || "{}").session_token; }
  catch { return false; }
}

function DiffViewer({ before, after }) {
  const parts = diffLines(before || "", after || "");
  return (
    <div className="grid md:grid-cols-2 gap-2 text-xs font-mono leading-relaxed" data-testid="diff-view">
      <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 overflow-x-auto max-h-[60vh] overflow-y-auto">
        <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-2 sticky top-0 bg-zinc-950 pb-1">Previous version</p>
        <pre className="whitespace-pre-wrap break-words">
          {parts.map((p, i) => p.added ? null : (
            <span key={i} className={p.removed ? "bg-red-500/15 text-red-200 block" : "text-zinc-400 block"}>
              {p.removed ? "− " : "  "}{p.value}
            </span>
          ))}
        </pre>
      </div>
      <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 overflow-x-auto max-h-[60vh] overflow-y-auto">
        <p className="text-zinc-500 text-[10px] uppercase tracking-wider mb-2 sticky top-0 bg-zinc-950 pb-1">Newer version</p>
        <pre className="whitespace-pre-wrap break-words">
          {parts.map((p, i) => p.removed ? null : (
            <span key={i} className={p.added ? "bg-emerald-500/15 text-emerald-200 block" : "text-zinc-400 block"}>
              {p.added ? "+ " : "  "}{p.value}
            </span>
          ))}
        </pre>
      </div>
    </div>
  );
}

export default function SopLibraryPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(null); // loaded full SOP
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [isPro, setIsPro] = useState(false);

  // Version history modal state
  const [historySopId, setHistorySopId] = useState(null);
  const [historyTitle, setHistoryTitle] = useState("");
  const [historyVersions, setHistoryVersions] = useState(null); // { current, history }
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyErr, setHistoryErr] = useState("");
  const [selectedVersions, setSelectedVersions] = useState([]); // up to 2 version numbers
  const [versionMd, setVersionMd] = useState({}); // {versionNumber: markdown}
  const [restoring, setRestoring] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);

  async function load() {
    setLoading(true); setErr("");
    if (!isLoggedIn()) {
      setErr("Please log in to view your SOP library.");
      setLoading(false);
      return;
    }
    try {
      const [libRes, proRes] = await Promise.all([
        axios.get(`${API}/sop-studio/library`, { headers: authHeaders() }),
        axios.get(`${API}/sop-studio/pro-status`, { headers: authHeaders() }).catch(() => ({ data: { is_pro: false } })),
      ]);
      setItems(libRes.data.items || []);
      setIsPro(!!proRes.data.is_pro);
    } catch (e) {
      setErr(e?.response?.status === 401 ? "Session expired. Please log in again." : "Could not load library.");
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function openPreview(sopId) {
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${API}/sop-studio/library/${sopId}`, { headers: authHeaders() });
      setPreview(res.data);
    } catch { setErr("Could not open SOP."); }
    finally { setPreviewLoading(false); }
  }

  async function deleteSop(sopId) {
    if (!window.confirm("Delete this SOP permanently? This cannot be undone.")) return;
    setDeletingId(sopId);
    try {
      await axios.delete(`${API}/sop-studio/library/${sopId}`, { headers: authHeaders() });
      setItems(prev => prev.filter(x => x.sop_id !== sopId));
      if (preview?.sop_id === sopId) setPreview(null);
    } catch { setErr("Could not delete SOP."); }
    finally { setDeletingId(null); }
  }

  async function openHistory(sopId, title) {
    if (!isPro) { setShowUpgrade(true); return; }
    setHistorySopId(sopId); setHistoryTitle(title);
    setHistoryVersions(null); setHistoryErr(""); setSelectedVersions([]); setVersionMd({});
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/sop-studio/library/${sopId}/versions`, { headers: authHeaders() });
      setHistoryVersions(res.data);
      // Auto-select the two most recent (current + previous if any)
      const current = res.data.current.version_number;
      const prev = res.data.history?.[0]?.version_number;
      const initial = prev ? [prev, current] : [current];
      setSelectedVersions(initial);
      // Pre-fetch markdowns
      await Promise.all(initial.map(v => loadVersionMarkdown(sopId, v)));
    } catch (e) {
      setHistoryErr(e?.response?.status === 402 ? "Pro feature." : (e?.response?.data?.detail || "Could not load versions."));
    } finally { setHistoryLoading(false); }
  }

  function closeHistory() {
    setHistorySopId(null); setHistoryVersions(null); setSelectedVersions([]); setVersionMd({}); setHistoryErr("");
  }

  async function loadVersionMarkdown(sopId, versionNumber) {
    if (versionMd[versionNumber]) return;
    try {
      const res = await axios.get(`${API}/sop-studio/library/${sopId}/versions/${versionNumber}`, { headers: authHeaders() });
      setVersionMd(prev => ({ ...prev, [versionNumber]: res.data.sop_markdown }));
    } catch { /* ignore */ }
  }

  function toggleVersion(versionNumber) {
    setSelectedVersions(prev => {
      if (prev.includes(versionNumber)) return prev.filter(v => v !== versionNumber);
      const next = [...prev, versionNumber].slice(-2);
      // Pre-fetch this version's markdown
      if (historySopId) loadVersionMarkdown(historySopId, versionNumber);
      return next.sort((a, b) => a - b);
    });
  }

  async function restoreVersion(versionNumber) {
    if (!window.confirm(`Restore version ${versionNumber} as the current SOP? The current version will be saved to history first.`)) return;
    setRestoring(true);
    try {
      await axios.post(`${API}/sop-studio/library/${historySopId}/restore/${versionNumber}`, {}, { headers: authHeaders() });
      // Refresh library + reopen history
      await load();
      const updatedItem = (await axios.get(`${API}/sop-studio/library`, { headers: authHeaders() })).data.items.find(x => x.sop_id === historySopId);
      if (updatedItem) await openHistory(historySopId, updatedItem.title);
    } catch (e) {
      setHistoryErr(e?.response?.data?.detail || "Restore failed.");
    } finally { setRestoring(false); }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <header className="border-b border-zinc-800/60 bg-zinc-950/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/ai-tools/sop-studio" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm" data-testid="back-to-studio">
            <ArrowLeft size={16} /> SOP Studio
          </Link>
          <div className="flex items-center gap-2" style={{ color: ACCENT }}>
            <Brain size={18} />
            <span className="font-semibold tracking-tight text-sm">My SOP Library</span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
              <Library size={28} style={{ color: ACCENT }} /> My SOP Library
            </h1>
            <p className="text-zinc-400 text-sm mt-2 flex items-center gap-2 flex-wrap">
              {loading ? "Loading…" : `${items.length} SOP${items.length === 1 ? "" : "s"} saved`}
              {isPro && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#7EE787]/15 border border-[#7EE787]/40 text-[#7EE787] uppercase tracking-wider font-bold" data-testid="pro-badge"><Crown size={10} /> Pro</span>}
              {isLoggedIn() && !isPro && (
                <Link to="/pricing#sop-studio" className="text-[10px] px-1.5 py-0.5 rounded-full bg-zinc-800 border border-zinc-700 text-zinc-300 hover:text-white hover:border-[#7EE787]/40 inline-flex items-center gap-1" data-testid="upgrade-pill">
                  <Crown size={10} /> Upgrade to Pro
                </Link>
              )}
            </p>
          </div>
          <button onClick={() => navigate("/ai-tools/sop-studio")}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-black font-bold text-sm"
                  style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
                  data-testid="new-sop-btn">
            <Plus size={14} /> New SOP
          </button>
        </div>

        {err && <div className="bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm p-3 rounded-lg mb-6" data-testid="library-error">{err}</div>}

        {loading ? (
          <div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading your SOPs…</div>
        ) : items.length === 0 && !err ? (
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-2xl p-10 text-center" data-testid="empty-library">
            <FileText size={40} className="mx-auto text-zinc-700 mb-3" />
            <p className="text-zinc-300 text-base font-semibold mb-1">No saved SOPs yet</p>
            <p className="text-zinc-500 text-sm mb-5">Generate your first SOP in the Studio and click Save to add it here.</p>
            <Link to="/ai-tools/sop-studio"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-black font-bold text-sm"
                  style={{ background: `linear-gradient(90deg, ${ACCENT}, #43A047)` }}>
              <Plus size={14} /> Create a SOP
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="library-grid">
            {items.map(it => (
              <div key={it.sop_id}
                   className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4 hover:border-[#7EE787]/40 transition-colors group"
                   data-testid={`library-card-${it.sop_id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <FileText size={16} style={{ color: ACCENT }} className="shrink-0 mt-0.5" />
                  <button onClick={() => deleteSop(it.sop_id)}
                          disabled={deletingId === it.sop_id}
                          className="text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                          data-testid={`delete-${it.sop_id}`}
                          title="Delete SOP">
                    {deletingId === it.sop_id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  </button>
                </div>
                <p className="text-white text-sm font-semibold line-clamp-2 mb-1">{it.title}</p>
                <div className="flex items-center gap-2 text-zinc-500 text-[11px] mb-3 flex-wrap">
                  {it.industry && <span className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300">{it.industry}</span>}
                  <span>v{it.version}</span>
                  <span>· {it.word_count} words</span>
                </div>
                <div className="flex items-center justify-between text-[11px] text-zinc-500">
                  <span className="inline-flex items-center gap-1"><Calendar size={11} /> {new Date(it.updated_at).toLocaleDateString()}</span>
                  <div className="flex items-center gap-2">
                    {it.version > 1 && (
                      <button onClick={() => openHistory(it.sop_id, it.title)}
                              className={`inline-flex items-center gap-1 ${isPro ? "text-zinc-300 hover:text-[#7EE787]" : "text-zinc-600 hover:text-zinc-300"}`}
                              data-testid={`history-${it.sop_id}`}
                              title={isPro ? "View version history" : "Pro feature — click to upgrade"}>
                        {isPro ? <History size={11} /> : <Lock size={11} />} History
                      </button>
                    )}
                    <button onClick={() => openPreview(it.sop_id)}
                            className="text-[#7EE787] hover:underline inline-flex items-center gap-1"
                            data-testid={`open-${it.sop_id}`}>
                      <Eye size={11} /> Open
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="preview-modal">
          <div className="bg-zinc-950 border border-zinc-800 rounded-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 p-4 border-b border-zinc-900">
              <div>
                <p className="text-white font-bold text-base">{preview.title}</p>
                <p className="text-zinc-500 text-[11px]">v{preview.version} · updated {new Date(preview.updated_at).toLocaleString()}</p>
              </div>
              <button onClick={() => setPreview(null)}
                      className="text-zinc-400 hover:text-white p-1 rounded hover:bg-zinc-900"
                      data-testid="close-preview">
                <X size={18} />
              </button>
            </div>
            <div className="overflow-y-auto p-6 prose prose-invert prose-h1:text-2xl prose-h1:font-bold prose-h2:text-lg prose-h2:font-bold prose-h2:text-[#7EE787] prose-headings:text-white prose-p:text-zinc-300 prose-li:text-zinc-300 prose-strong:text-white max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{preview.sop_markdown}</ReactMarkdown>
            </div>
          </div>
        </div>
      )}
      {previewLoading && <div className="fixed bottom-6 right-6 bg-zinc-900 border border-zinc-800 text-zinc-300 text-sm px-3 py-2 rounded-lg flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>}

      {/* Version History modal (Pro) */}
      {historySopId && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeHistory} data-testid="history-modal">
          <div className="bg-zinc-950 border border-[#7EE787]/30 rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 p-4 border-b border-zinc-900">
              <div>
                <p className="text-white font-bold text-base flex items-center gap-2"><History size={18} className="text-[#7EE787]" /> Version history</p>
                <p className="text-zinc-500 text-[11px]">{historyTitle}</p>
              </div>
              <button onClick={closeHistory}
                      className="text-zinc-400 hover:text-white p-1 rounded hover:bg-zinc-900"
                      data-testid="close-history">
                <X size={18} />
              </button>
            </div>

            {historyLoading ? (
              <div className="p-8 text-zinc-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading versions…</div>
            ) : historyErr ? (
              <div className="p-8 text-amber-400 text-sm">{historyErr}</div>
            ) : historyVersions ? (
              <div className="grid md:grid-cols-[200px_1fr] flex-1 overflow-hidden">
                {/* Timeline sidebar */}
                <aside className="border-r border-zinc-900 overflow-y-auto p-3 space-y-1.5" data-testid="version-timeline">
                  <p className="text-zinc-500 text-[10px] uppercase tracking-wider px-2 mb-1">Click 1 to preview · 2 to compare</p>
                  {[historyVersions.current, ...historyVersions.history].map((v, i) => {
                    const sel = selectedVersions.includes(v.version_number);
                    const isCurrent = v.is_current;
                    return (
                      <button key={v.version_id || i}
                              onClick={() => toggleVersion(v.version_number)}
                              className={`w-full text-left p-2 rounded-lg border text-xs transition-colors ${sel ? "border-[#7EE787]/60 bg-[#7EE787]/10" : "border-zinc-800 hover:border-zinc-700 bg-zinc-900/40"}`}
                              data-testid={`version-${v.version_number}`}>
                        <div className="flex items-center gap-1.5">
                          <span className="text-white font-bold">v{v.version_number}</span>
                          {isCurrent && <span className="text-[9px] uppercase tracking-wider text-[#7EE787] bg-[#7EE787]/15 border border-[#7EE787]/40 px-1 rounded">Current</span>}
                          {sel && <Check size={11} className="text-[#7EE787] ml-auto" />}
                        </div>
                        <p className="text-zinc-500 text-[10px] mt-0.5">{new Date(v.snapshotted_at).toLocaleString()}</p>
                        <p className="text-zinc-400 text-[10px] mt-0.5">{v.word_count} words</p>
                      </button>
                    );
                  })}
                </aside>

                {/* Diff / preview pane */}
                <div className="overflow-y-auto p-4 flex flex-col gap-3">
                  {selectedVersions.length === 0 && (
                    <p className="text-zinc-500 text-sm">Select a version from the timeline to preview, or two versions to compare.</p>
                  )}
                  {selectedVersions.length === 1 && versionMd[selectedVersions[0]] !== undefined && (
                    <>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <p className="text-white text-sm font-bold">Version {selectedVersions[0]}</p>
                        {!historyVersions.current.version_number === selectedVersions[0] && (
                          <button onClick={() => restoreVersion(selectedVersions[0])} disabled={restoring}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-black font-bold text-xs disabled:opacity-50"
                                  style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
                                  data-testid="restore-btn">
                            {restoring ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Restore this version
                          </button>
                        )}
                      </div>
                      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4 prose prose-invert prose-h1:text-xl prose-h1:font-bold prose-h2:text-base prose-h2:font-bold prose-h2:text-[#7EE787] prose-headings:text-white prose-p:text-zinc-300 prose-li:text-zinc-300 prose-strong:text-white max-w-none max-h-[60vh] overflow-y-auto">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{versionMd[selectedVersions[0]]}</ReactMarkdown>
                      </div>
                    </>
                  )}
                  {selectedVersions.length === 2 && versionMd[selectedVersions[0]] !== undefined && versionMd[selectedVersions[1]] !== undefined && (
                    <>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <p className="text-white text-sm font-bold">Comparing v{selectedVersions[0]} → v{selectedVersions[1]}</p>
                        {historyVersions.current.version_number !== selectedVersions[0] && (
                          <button onClick={() => restoreVersion(selectedVersions[0])} disabled={restoring}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-black font-bold text-xs disabled:opacity-50"
                                  style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
                                  data-testid="restore-btn">
                            {restoring ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Restore v{selectedVersions[0]}
                          </button>
                        )}
                      </div>
                      <DiffViewer before={versionMd[selectedVersions[0]]} after={versionMd[selectedVersions[1]]} />
                    </>
                  )}
                  {selectedVersions.length > 0 && selectedVersions.some(v => versionMd[v] === undefined) && (
                    <div className="text-zinc-500 text-xs flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading version markdown…</div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Upgrade-to-Pro modal */}
      {showUpgrade && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowUpgrade(false)} data-testid="upgrade-modal">
          <div className="bg-zinc-950 border border-[#7EE787]/30 rounded-2xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <Crown size={20} className="text-[#7EE787]" />
                <p className="text-white font-bold text-base">Unlock SOP Studio Pro</p>
              </div>
              <button onClick={() => setShowUpgrade(false)} className="text-zinc-400 hover:text-white p-1" data-testid="close-upgrade"><X size={16} /></button>
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed">
              Version history with side-by-side diff, one-click restore, and Impact Analysis are Pro-only.
            </p>
            <ul className="text-zinc-300 text-xs space-y-1.5 list-disc list-inside">
              <li>Edit saved SOPs (creates a new version each time)</li>
              <li>Browse and compare up to 20 prior versions</li>
              <li>One-click restore to any version</li>
              <li>AI Impact Analysis on every change</li>
            </ul>
            <div className="bg-gradient-to-br from-[#7EE787]/10 to-[#43A047]/5 border border-[#7EE787]/30 rounded-xl p-3 flex items-center justify-between">
              <div>
                <p className="text-white text-2xl font-bold">₹999<span className="text-zinc-500 text-sm font-normal">/mo</span></p>
                <p className="text-zinc-500 text-[11px]">or ₹9,990/yr (save 17%)</p>
              </div>
              <Link to="/pricing#sop-studio"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-black font-bold text-xs"
                    style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
                    data-testid="upgrade-cta">
                Upgrade <Edit3 size={12} />
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
